import { createSelector } from '@reduxjs/toolkit';
import { message } from 'antd';
import { put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { logger } from 'services';
import {
  DeletePhaseInProjectTemplate,
  deletePhaseInProjectTemplate,
} from '../../actions';
import {
  getProjectTemplatePhaseIds,
  selectActiveTemplateId,
  selectTemplateProjectPhase,
  selectTemplateTask,
} from '../../selectors';
import {
  deleteProjectTemplateProjectPhase,
  setProjectTemplateActiveRecord,
} from '../../slices';
import { ProjectTemplateActiveRecord } from '../../types';
import { isSameTaskOrChildTask } from '../task/delete';

const selectActiveRecord = createSelector(
  (state: RootState) => state.templates.project.activeRecord,
  (
    activeRecord: ProjectTemplateActiveRecord | undefined,
  ): ProjectTemplateActiveRecord | undefined => activeRecord,
);

function* isChildTaskOfProjectPhase(projectPhase, activeTaskId) {
  const { tasks } = projectPhase;

  for (let i = 0; i < tasks.length; i += 1) {
    if (tasks[i] === activeTaskId) {
      return true;
    }

    const task = yield select(selectTemplateTask(tasks[i]));

    if (yield isSameTaskOrChildTask(task, activeTaskId)) {
      return true;
    }
  }

  return false;
}

function* deleteProjectPhaseInTemplateSaga(
  action: StoreActionProps<DeletePhaseInProjectTemplate>,
) {
  try {
    const {
      payload: { projectPhaseId },
    } = action;

    const projectPhase = yield select(
      selectTemplateProjectPhase(projectPhaseId),
    );
    const templateId = yield select(selectActiveTemplateId);
    const phaseIds = yield select(getProjectTemplatePhaseIds(templateId));
    const activeRecord = yield select(selectActiveRecord);

    let shouldChangeActiveRecord = false;

    if (activeRecord) {
      if (activeRecord.type === 'PROJECT_PHASE') {
        shouldChangeActiveRecord = projectPhaseId === activeRecord.id;
      } else if (activeRecord.type === 'TASK') {
        shouldChangeActiveRecord = yield isChildTaskOfProjectPhase(
          projectPhase,
          activeRecord.id,
        );
      }
    }

    yield put(deleteProjectTemplateProjectPhase({ projectPhaseId }));

    if (shouldChangeActiveRecord) {
      yield put(
        setProjectTemplateActiveRecord({
          id: parseInt(phaseIds[0], 10),
          type: 'PROJECT_PHASE',
        }),
      );
    }
  } catch (error) {
    logger.error('an error deleting a project phase from the template', {
      error,
    });

    message.error(
      'There was an error deleting a project phase from the template',
    );
  }
}

export function* watchDeletePhaseInProjectTemplateSaga(): Generator {
  yield takeLatest(
    deletePhaseInProjectTemplate.type,
    deleteProjectPhaseInTemplateSaga,
  );
}
